<template>
  <div class="optimization-filter">
    <div class="filter-grid">
      <!--Applications -->
      <div class="filter-item">
        <h4>{{ i18n.t('applications') }}</h4>

        <SmSelect
          v-model:selection="selectedApplicationOptions"
          :options="applicationOptions"
          :label="i18n.t('selectApplication')"
          :multiselect="true"
          searchable
          @change="
            (selection) => {
              if (selection) selectApplications(selection)
            }
          " />
      </div>
      <div class="filter-item">
        <h4>{{ i18n.t('optimizationType') }}</h4>
        <SmSelect
          v-model:selection="selectedTypeOptions"
          :options="typeOptions"
          :label="i18n.t('selectType')"
          :multiselect="true"
          searchable
          @change="
            (selection) => {
              if (selection) selectType(selection)
            }
          " />
      </div>
      <div class="h-1"></div>
      <div class="flex flex-row items-center justify-between gap-2">
        <span>{{ i18n.t('hideExpenseless') }}</span>
        <SmSwitch v-model="hideExpenseless" style="font-size: 0.75rem" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n'

  import { OptimizationType, SoftwareOut } from '@/client'

  import { Option } from '@/components/sm/SmSelect.vue'
  import { OptimizationFilters } from '@/stores/optimization/optimization.filter'

  const i18n = useI18n()

  interface Props {
    applications: SoftwareOut[]
    filter: OptimizationFilters
  }

  const props = defineProps<Props>()
  const emit = defineEmits(['update:filter'])

  // ----------------------------------------------------------------------
  // Application filter
  // ----------------------------------------------------------------------

  const applicationOptions = computed(() => {
    return props.applications
      .map((app) => {
        return {
          label: app.display_name,
          value: app._id,
        }
      })
      .toSorted((a, b) => {
        return a.label.localeCompare(b.label)
      })
  })

  const selectedApplications = ref<string[]>(props.filter.application_ids ?? [])
  const selectedApplicationOptions = ref<Option<string>[]>(
    applicationOptions.value.filter((app) =>
      props.filter.application_ids?.includes(app.value)
    )
  )

  function selectApplications(selection: Option<string>[]) {
    selectedApplicationOptions.value = selection
    selectedApplications.value = selection.map((s) => s.value)
  }

  watch(selectedApplications, () => {
    if (selectedApplications.value.length === 0) {
      emit('update:filter', { application_ids: undefined })
    } else {
      emit('update:filter', { application_ids: selectedApplications.value })
    }
  })

  // ----------------------------------------------------------------------
  // Typ filter
  // ----------------------------------------------------------------------

  const typeOptions = computed(() => {
    return Object.values(OptimizationType)
      .map((type) => {
        return {
          label: i18n.t(`optimizationNames.${type}`),
          value: type,
        }
      })
      .toSorted((a, b) => {
        return a.label.localeCompare(b.label)
      })
  })

  const selectedType = ref<string[]>(props.filter.type ?? [])
  const selectedTypeOptions = ref<Option<string>[]>(
    typeOptions.value.filter((type) => props.filter.type?.includes(type.value))
  )

  function selectType(selection: Option<string>[]) {
    selectedTypeOptions.value = selection
    selectedType.value = selection.map((s) => s.value)
  }

  watch(selectedType, () => {
    if (selectedType.value.length === 0) {
      emit('update:filter', { type: undefined })
    } else {
      emit('update:filter', { type: selectedType.value })
    }
  })

  // ----------------------------------------------------------------------
  // Boolean filter
  // ----------------------------------------------------------------------

  const hideExpenseless = ref<boolean>(props.filter.hideExpenseless ?? false)

  watch(hideExpenseless, () => {
    emit('update:filter', { hideExpenseless: hideExpenseless.value })
  })
</script>

<style lang="scss" scoped>
  .filter-grid {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 1rem;
  }

  .filter-item {
    display: flex;
    flex-direction: column;

    h4 {
      font-weight: 400;
      margin-bottom: 0.5rem;
    }
  }

  .dropdown-select {
    display: flex;
    align-items: center;

    padding: 0.5rem 1rem;

    border-radius: 8px;
    font-size: 1rem;
  }

  .width-2 {
    grid-column: span 2;
  }

  .dropdown-item {
    display: grid;
    grid-template-columns: 1fr 10px;

    font-size: 0.9rem;
  }
  .optimization-filter {
    cursor: pointer;
    pointer-events: all;

    min-width: 200px;
  }
</style>
